/**
 * ZEN ERP — Teklifler Listesi Modülü
 * QuoteBuilderPage.jsx'ten çıkarıldı.
 */
import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { Plus, RefreshCw, FileSignature, MapPin, History, Trash2, ArrowLeft, Search, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { AuthCtx } from "../appContexts";
import api from "../api";
import { fmt } from "../appUtils";
import { QUOTE_BRANDS, BrandSelectModal, QuotePrintModal } from "../quotes";
import { QuoteBuilderPage } from "./QuoteBuilderPage";
import { QuoteViewsModal } from "../components/QuoteViewsModal";
import { QuoteRevisionHistoryModal } from "../components/QuoteRevisionHistoryModal";
import { PrintPreviewModal } from "../components/PrintPreviewModal";
import { TailorSheetButton } from "../components/TailorSheetButton";
import ShareMapModule from "../shareMap";
import { ModuleIcon } from "../components/layout/Sidebar";
import ZenPagination from "../components/ZenPagination";

export function QuotesListModule({ onNavigate }) {
  const { user } = useContext(AuthCtx);
  const isDealer = user?.role === "dealer";
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewQuote, setPreviewQuote] = useState(null);
  const [tailorQuote, setTailorQuote] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderBrand, setBuilderBrand] = useState("zen");
  const [editingQuote, setEditingQuote] = useState(null);
  const [isRevision, setIsRevision] = useState(false);
  const [showBrandSelect, setShowBrandSelect] = useState(false);
  const [views, setViews] = useState({});
  const [seenQuote, setSeenQuote] = useState(null);
  const [historyQuote, setHistoryQuote] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [listRates, setListRates] = useState({ eurToTl: 38, usdToTl: 34 });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => { setPage(1); }, [search]);

  const filteredQuotes = useMemo(() => {
    if (!search.trim()) return quotes;
    const q = search.toLowerCase();
    return quotes.filter(qt =>
      (qt.customerName || "").toLowerCase().includes(q) ||
      String(qt.quoteNo || "").toLowerCase().includes(q) ||
      (qt.items || []).some(item => (item.product || "").toLowerCase().includes(q))
    );
  }, [quotes, search]);
  const paginatedQuotes = filteredQuotes.slice((page - 1) * pageSize, page * pageSize);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: qData }, { data: rData }] = await Promise.all([api.get("/quotes"), api.get("/rates").catch(() => ({ data: {} }))]);
      // Arşivli revizyonlar listede GÖSTERİLMEZ — geçmişleri "Revizyon Geçmişi" penceresindedir
      setQuotes((qData || []).filter(q => !q.isArchived && !q.archived));
      if (rData?.eurToTl) setListRates(rData);
      try {
        const { data: v } = await api.get("/share-views", { params: { refType: "quote" } });
        setViews(v || {});
      } catch (e) { /* takip verisi opsiyonel */ }
    } catch (e) { toast.error("Teklifler yüklenemedi"); }
    setLoading(false);
  };
  const prevViewsRef = useRef({});

  useEffect(() => {
    load();
    // Her 30 sn'de görüntülenme verisini güncelle (görüldü rozeti canlı kalır)
    const viewsTimer = setInterval(async () => {
      try {
        const { data: v } = await api.get("/share-views", { params: { refType: "quote" } });
        const newViews = v || {};
        // Yeni görüntülenme var mı? → TEK bir canlı bildirim (aynı id ile güncellenir, üst üste yığılmaz)
        Object.entries(newViews).forEach(([qid, info]) => {
          const prev = prevViewsRef.current[qid];
          if (info.viewCount > (prev?.viewCount || 0)) {
            // Görüntülenme bildirimi kapatıldı (kullanıcı isteği)
          }
        });
        prevViewsRef.current = newViews;
        setViews(newViews);
      } catch (_) {}
    }, 30000);
    return () => clearInterval(viewsTimer);
  }, []);

  // Tek satırlık "Son Görüntüleme" etiketi: tarih + saat + (varsa) şehir
  const lastViewLabel = (v) => {
    if (!v?.lastViewedAt) return null;
    const d = new Date(v.lastViewedAt);
    const now = new Date();
    const dateStr = d.toDateString() === now.toDateString()
      ? "Bugün" : d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" });
    const timeStr = d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
    const addrParts = (v.gps?.address || "").split(",").map(s => s.trim()).filter(Boolean);
    const city = addrParts.length > 1 ? addrParts[addrParts.length - 2] : addrParts[0];
    return `Son Görüntüleme: ${dateStr} ${timeStr}${city ? " - " + city : ""}`;
  };

  const convert = async (q) => {
    if (!window.confirm(`Teklif #${q.quoteNo} siparişe dönüştürülsün mü? Bu işlem geri alınamaz.`)) return;
    try {
      const { data } = await api.post(`/quotes/${q.id}/convert-to-order`);
      const warnings = data?.warnings || [];
      if (warnings.length > 0) {
        toast.warning(`Sipariş oluşturuldu — ${warnings.length} uyarı`, {
          description: warnings.join(" · "),
          duration: 8000,
        });
      } else {
        toast.success(`Sipariş #${data?.orderNo} oluşturuldu — İmalata Hazır`, { duration: 4000 });
      }
      load();
      if (onNavigate) setTimeout(() => onNavigate("orders"), 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Dönüştürülemedi"); }
  };
  const remove = async (q) => {
    if (!window.confirm(`Teklif #${q.quoteNo} arşive taşınsın mı? Arşiv bölümünden geri alabilirsiniz.`)) return;
    try {
      await api.delete(`/quotes/${q.id}`);
      toast.success("Arşive taşındı", { description: "Arşiv sayfasından geri yükleyebilirsiniz." });
      load();
    } catch (e) { toast.error("Silinemedi"); }
  };
  const openRevise = (q) => { setEditingQuote(q); setIsRevision(true); setBuilderBrand(q.brand || "zen"); setShowBuilder(true); };

  const STATUS_LABEL = { acik: "Açık", donusturuldu: "Siparişe Dönüştürüldü", iptal: "İptal" };
  const STATUS_COLOR = { acik: "bg-slate-500/15 text-slate-500", donusturuldu: "bg-emerald-500/15 text-emerald-600", iptal: "bg-rose-500/15 text-rose-500" };

  return (
    <div className="space-y-4 zen-anim-up" data-testid="quotes-list-page">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold flex items-center gap-2.5">
          <ModuleIcon tab="quotes" icon={showMap ? MapPin : FileSignature} size="w-8 h-8" iconClass="w-4 h-4" radius="rounded-[10px]" />
          {showMap ? "Açılış Haritası" : "Verilen Teklifler"}
        </h2>
        <div className="flex gap-2">
          {showMap ? (
            <button onClick={() => setShowMap(false)} className="zen-btn-ghost !text-xs" data-testid="quotes-back-from-map-btn"><ArrowLeft className="w-3.5 h-3.5" /> Tekliflere Dön</button>
          ) : (
            <>
              <button onClick={() => setShowMap(true)} className="zen-btn-ghost !text-xs" data-testid="quotes-show-map-btn" title="Müşterilerin gönderdiğiniz teklif PDF'lerini ne zaman açtığını gösterir"><MapPin className="w-3.5 h-3.5" /> Açılış Haritası</button>
              <button onClick={load} className="zen-btn-ghost !text-xs" data-testid="quotes-refresh-btn"><RefreshCw className="w-3.5 h-3.5" /> Yenile</button>
              <button onClick={() => setShowBrandSelect(true)} className="zen-btn-primary !text-xs" data-testid="quotes-new-btn"><Plus className="w-3.5 h-3.5" /> Yeni Teklif</button>
            </>
          )}
        </div>
      </div>

      {/* ── Anlık Arama Çubuğu ─────────────────────────────────── */}
      {!showMap && (
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
          <input
            data-testid="quotes-search-input"
            type="text"
            placeholder="Müşteri, Ürün veya Referans No ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="zen-input !pl-9 !pr-8 !py-2 w-full !text-sm"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
      {showMap ? (
        <ShareMapModule />
      ) : loading ? (
        <div className="text-sm text-slate-500 p-6 text-center">Yükleniyor...</div>
      ) : quotes.length === 0 ? (
        <div className="zen-panel p-10 text-center">
          <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <div className="text-sm font-semibold text-slate-600 dark:text-slate-300">Henüz teklif oluşturulmadı</div>
          <div className="text-xs text-slate-500 mt-1.5 max-w-sm mx-auto leading-relaxed">
            Sağ üstteki <b>“Yeni Teklif”</b> ile başlayın. Teklifler müşteriye PDF olarak gönderilir;
            <b> “Açılış Haritası”</b>ndan müşterinin PDF'i ne zaman açtığını görebilirsiniz.
          </div>
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="zen-panel p-6 text-center text-sm text-slate-500">
          "<span className="font-semibold text-slate-300">{search}</span>" ile eşleşen teklif bulunamadı.
        </div>
      ) : (
        <div className="zen-panel overflow-x-auto">
          {search && (
            <div className="px-3 pt-2.5 pb-1 text-[11px] text-slate-500">
              <span className="font-semibold text-slate-300">{filteredQuotes.length}</span> sonuç / {quotes.length} toplam teklif
            </div>
          )}
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 dark:border-[#22304A] bg-slate-50 dark:bg-[#0C1220] text-[10px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-3 py-2">Teklif No</th>
                <th className="text-left px-3 py-2">Tarih</th>
                <th className="text-left px-3 py-2">Marka</th>
                <th className="text-left px-3 py-2">Müşteri</th>
                <th className="text-right px-3 py-2">Toplam</th>
                <th className="text-center px-3 py-2">Görüldü</th>
                <th className="text-center px-3 py-2">Durum</th>
                <th className="text-right px-3 py-2">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {paginatedQuotes.map((q) => (
                <tr key={q.id} className="border-b border-slate-100 dark:border-[#22304A]/50 hover:bg-slate-50 dark:hover:bg-[#0C1220]/60" data-testid={`quote-row-${q.quoteNo}`}>
                  <td className="px-3 py-2.5 font-bold zen-mono">
                    #{q.quoteNo}
                    <div className="mt-1"><TailorSheetButton record={q} testId={`quote-tailor-sheet-${q.quoteNo}`} onClick={() => setTailorQuote(q)} /></div>
                    {q.revisionNo > 1 && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">Rev {q.revisionNo}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-slate-500">{new Date(q.createdAt).toLocaleDateString("tr-TR")}</td>
                  <td className="px-3 py-2.5">{(QUOTE_BRANDS[q.brand] || QUOTE_BRANDS.zen).name}</td>
                  <td className="px-3 py-2.5 font-semibold">{q.customerName}</td>
                  <td className="px-3 py-2.5 text-right zen-mono font-bold">{fmt(q.total, q.currency)}</td>
                  <td className="px-3 py-2.5 text-center whitespace-nowrap" data-testid={`quote-seen-${q.quoteNo}`}>
                    {views[q.id]?.viewCount ? (
                      <button
                        onClick={() => setSeenQuote(q)}
                        data-testid={`quote-seen-btn-${q.quoteNo}`}
                        className="zen-chip bg-emerald-500/15 text-emerald-600 !text-[10px] hover:bg-emerald-500/25 transition-colors"
                        title={`Görüntüleme Geçmişi (${views[q.id].viewCount} kez) — tümünü gör`}
                      >
                        {lastViewLabel(views[q.id]) || "görüldü ✓"}
                      </button>
                    ) : (
                      <span className="text-slate-400 text-[10px]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center"><span className={`zen-chip ${STATUS_COLOR[q.status] || ""}`}>{STATUS_LABEL[q.status] || q.status}</span></td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => setPreviewQuote(q)} data-testid={`quote-view-${q.quoteNo}`} className="zen-btn-ghost !text-[10px] !py-1 !px-2 mr-1">Görüntüle</button>

                    {q.status === "acik" && (
                      <>
                        {!isDealer && <button onClick={() => openRevise(q)} data-testid={`quote-revise-${q.quoteNo}`} className="zen-btn-ghost !text-[10px] !py-1 !px-2 mr-1 text-amber-600">Revize Et</button>}
                        {!isDealer && <button onClick={() => convert(q)} data-testid={`quote-convert-${q.quoteNo}`} className="zen-btn-ghost !text-[10px] !py-1 !px-2 mr-1 text-emerald-600">Siparişe Dönüştür</button>}
                      </>
                    )}
                    <button onClick={() => setHistoryQuote(q)} data-testid={`quote-history-${q.quoteNo}`} className="zen-btn-ghost !text-[10px] !py-1 !px-1.5 mr-1 text-slate-400" title="Revizyon geçmişi">
                      <History className="w-3.5 h-3.5" />
                    </button>
                    {!isDealer && <button onClick={() => remove(q)} data-testid={`quote-delete-${q.quoteNo}`} className="p-1.5 text-rose-500 hover:bg-rose-500/10 rounded"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <ZenPagination total={filteredQuotes.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
        </div>
      )}
      {previewQuote && <QuotePrintModal quote={previewQuote} onClose={() => setPreviewQuote(null)} />}
      {tailorQuote && <PrintPreviewModal order={tailorQuote} refType="quote" initialDocType="tulfon" onClose={() => setTailorQuote(null)} />}
      {seenQuote && <QuoteViewsModal refType="quote" refId={seenQuote.id} title={`Teklif #${seenQuote.quoteNo}`} subtitle={seenQuote.customerName} onClose={() => setSeenQuote(null)} />}
      {historyQuote && <QuoteRevisionHistoryModal quoteNo={historyQuote.quoteNo} onClose={() => setHistoryQuote(null)} onPreview={(q) => { setHistoryQuote(null); setPreviewQuote(q); }} />}
      {showBrandSelect && (
        <BrandSelectModal onClose={() => setShowBrandSelect(false)} onSelect={(b) => { setBuilderBrand(b); setEditingQuote(null); setIsRevision(false); setShowBrandSelect(false); setShowBuilder(true); }} />
      )}
      {showBuilder && (
        <QuoteBuilderPage
          brand={builderBrand}
          editingQuote={editingQuote}
          isRevision={isRevision}
          onClose={() => { setShowBuilder(false); setEditingQuote(null); setIsRevision(false); }}
          onSaved={(q) => { setShowBuilder(false); setEditingQuote(null); setIsRevision(false); load(); setPreviewQuote(q); }}
        />
      )}
    </div>
  );
}
